import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { optionText } from '../lib/optionText';

function optionsFrom(children) {
  return React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement(child)) return [];
    if (child.type === 'option') {
      const label = optionText(child.props.label ?? child.props.children);
      return [{ value: String(child.props.value ?? label), label, disabled: !!child.props.disabled }];
    }
    return optionsFrom(child.props.children).map(option => ({ ...option, disabled: option.disabled || !!child.props.disabled }));
  });
}

const Chevron = () => <svg aria-hidden="true" width="12" height="8" viewBox="0 0 14 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-2 w-3 shrink-0 text-muted-foreground transition-transform"><path d="M1 1.5l6 6 6-6" /></svg>;
const SearchIcon = () => <svg aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>;
const CheckIcon = () => <svg aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;

// Keep a native select for form validation, reset, submission and existing change handlers.
export default function SearchableSelect({ children, className = '', value, defaultValue, onChange, disabled, required, triggerProps = {}, ...props }) {
  const native = useRef(null);
  const trigger = useRef(null);
  const forwardedTriggerRef = triggerProps.ref;
  const setTriggerRef = useCallback(node => {
    trigger.current = node;
    if (typeof forwardedTriggerRef === 'function') forwardedTriggerRef(node);
    else if (forwardedTriggerRef) forwardedTriggerRef.current = node;
  }, [forwardedTriggerRef]);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const [label, setLabel] = useState('Choose option');
  const listId = useId();
  const options = optionsFrom(children);
  const selectedValue = String(value ?? internalValue ?? options[0]?.value ?? '');
  const selected = options.find(option => option.value === selectedValue);
  const filtered = options.filter(option => option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  const available = filtered.filter(option => !option.disabled);

  useEffect(() => {
    const parentLabel = native.current?.labels?.[0] || native.current?.closest('label');
    const text = parentLabel && Array.from(parentLabel.childNodes).filter(node => node.nodeType === 3).map(node => node.textContent).join('').trim();
    if (text) setLabel(text);
    const form = native.current?.form;
    const reset = () => { setInternalValue(defaultValue); setOpen(false); };
    form?.addEventListener('reset', reset);
    return () => form?.removeEventListener('reset', reset);
  }, [defaultValue]);

  const choose = option => {
    if (option.disabled || native.current.matches(':disabled')) return;
    native.current.value = option.value;
    native.current.dispatchEvent(new Event('change', { bubbles: true }));
    setOpen(false);
    trigger.current?.focus();
  };
  const changeOpen = next => {
    if (next && native.current?.matches(':disabled')) return;
    setSearch(''); setActive(0); setOpen(next);
  };
  // Width utilities passed in className (w-48, max-w-xs, sm:w-64...) size the whole control, not just the inner button.
  const classes = className.split(/\s+/).filter(Boolean);
  const isWidth = c => /^(?:[a-z0-9-]+:)*(?:w|min-w|max-w)-/.test(c) && !/(?:^|:)w-full$/.test(c);
  const widthClass = classes.filter(isWidth).join(' ');
  const triggerClass = classes.filter(c => !isWidth(c)).join(' ');
  const hasExplicitWidth = classes.some(c => /^(?:[a-z0-9-]+:)*w-/.test(c) && !/(?:^|:)w-full$/.test(c));
  return <div className={`min-w-0 ${hasExplicitWidth ? '' : 'w-full'} ${widthClass}`}>
    <select {...props} ref={native} className="sr-only" tabIndex={-1} aria-hidden="true"
      value={value} defaultValue={defaultValue} disabled={disabled} required={required}
      onChange={event => { setInternalValue(event.target.value); onChange?.(event); }}
      onInvalid={event => { event.preventDefault(); props.onInvalid?.(event); changeOpen(true); trigger.current?.focus(); }}>
      {options.map((option, index) => <option key={`${option.value}-${index}`} value={option.value} disabled={option.disabled}>{option.label}</option>)}
    </select>
    <Popover open={open && !disabled} onOpenChange={changeOpen}>
      <PopoverTrigger asChild><button {...triggerProps} ref={setTriggerRef} type="button" disabled={disabled}
        onKeyDown={event => {
          triggerProps.onKeyDown?.(event);
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); changeOpen(true); }
        }}
        aria-label={props['aria-label'] || triggerProps['aria-label'] || label} aria-haspopup="listbox" aria-expanded={open && !disabled}
        className={`bk-trigger flex w-full items-center justify-between gap-2 text-left disabled:opacity-50 ${triggerClass}`}>
        <span className="truncate">{selected?.label || 'Choose…'}</span><Chevron />
      </button></PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[var(--radix-popover-trigger-width)] min-w-48 overflow-hidden rounded-xl border p-0 shadow-xl">
        <div className="relative border-b">
          <SearchIcon />
          <input autoFocus type="search" role="combobox" aria-label={`Search ${label}`} aria-expanded="true"
            aria-controls={listId} aria-autocomplete="list" aria-activedescendant={available[active] ? `${listId}-${options.indexOf(available[active])}` : undefined}
            className="h-11 w-full bg-transparent pl-9 pr-3 text-sm outline-none" placeholder="Search…" value={search}
            onChange={event => { setSearch(event.target.value); setActive(0); }}
            onKeyDown={event => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault(); setActive(index => Math.max(0, Math.min(available.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
              } else if (event.key === 'Enter') {
                event.preventDefault(); if (available[active]) choose(available[active]);
              }
            }} />
        </div>
        <div id={listId} role="listbox" aria-label={label} className="max-h-64 overflow-y-auto p-1.5">
          {!filtered.length && <p className="p-3 text-center text-sm text-muted-foreground">No matching options</p>}
          {filtered.map((option, index) => {
            const isSelected = option.value === selectedValue;
            return <div key={`${option.value}-${index}`} id={`${listId}-${options.indexOf(option)}`}
              role="option" aria-selected={isSelected} aria-disabled={option.disabled}
              className={`flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm ${option.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-accent'} ${isSelected ? 'bg-primary/10 font-semibold text-primary hover:bg-primary/10' : ''} ${available[active] === option && !isSelected ? 'bg-accent' : ''}`}
              onMouseDown={event => event.preventDefault()} onClick={() => choose(option)}>
              <span className="truncate">{option.label}</span>{isSelected && <CheckIcon />}
            </div>;
          })}
        </div>
      </PopoverContent>
    </Popover>
  </div>;
}
